/**
 * Splitting a chapter body into askable lines.
 *
 * A narrated chapter gets its lines from the alignment manifest — real
 * sentences, measured against the audio. A chapter that has never been narrated
 * has only the author's text, and a reader still has questions about it, so the
 * split has to happen here.
 *
 * Deliberately not the server's passage splitter. That one sizes windows for
 * retrieval (600 characters, `internal/reading/passage.go`); this one produces
 * what a finger can land on, which is a sentence. The ask endpoint takes the
 * sentence as *text* and locates it server-side, so the two never have to agree
 * on an offset — only on the words.
 */

/**
 * Terminators that end a sentence.
 *
 * `؟` and `؛` are the Arabic-script question mark and semicolon and are what
 * Persian actually uses; a splitter that only knew `?` would run a whole
 * Persian paragraph together. `۔` is the Urdu full stop, which appears in
 * imported text often enough to be worth a character.
 */
const TERMINATORS = ".!?…؟؛۔";

/** A line of a chapter: one sentence, or one blank gap between paragraphs. */
export interface Line {
  text: string;
  /** Blank lines are laid out and never armed — there is nothing to ask about. */
  blank: boolean;
  /**
   * This line opens a paragraph, so a newline goes before it.
   *
   * Carrying the break as a flag rather than inside the text is what lets the
   * view render each sentence as its own element — a pressable target — and
   * still reproduce the author's text exactly, which the test pins.
   */
  opensParagraph: boolean;
}

/**
 * splitLines cuts a body into askable sentences, preserving paragraph breaks.
 *
 * Paragraphs first, sentences within them: a paragraph break is a stronger
 * boundary than any punctuation, and splitting on terminators alone would glue
 * the last sentence of one paragraph to the first of the next whenever an
 * author ended a line without a full stop — which in dialogue is most of them.
 */
export function splitLines(body: string, language: string): Line[] {
  const known = titlesOf(language);
  const out: Line[] = [];
  for (const paragraph of body.split(/\n/)) {
    if (paragraph.trim() === "") {
      out.push({ text: paragraph, blank: true, opensParagraph: true });
      continue;
    }
    let first = true;
    for (const sentence of splitSentences(paragraph, known)) {
      out.push({ text: sentence, blank: false, opensParagraph: first });
      first = false;
    }
  }
  return out;
}

/**
 * splitSentences cuts one paragraph after each terminator.
 *
 * A run of terminators ends one sentence, not three: `؟!…` is one question
 * asked loudly. Trailing spaces stay with the sentence they follow so that
 * joining the result back together reproduces the paragraph exactly — which is
 * the property the test pins, because a splitter that drops a character is a
 * splitter that shows the reader text the author did not write.
 */
function splitSentences(paragraph: string, known: ReadonlySet<string>): string[] {
  const out: string[] = [];
  let start = 0;
  for (let i = 0; i < paragraph.length; i++) {
    if (!TERMINATORS.includes(paragraph[i]!) || titleEndsAt(known, paragraph, i)) continue;
    // Swallow the rest of the run, plus any closing quote or bracket that
    // belongs to this sentence rather than to the next one.
    let end = i + 1;
    while (end < paragraph.length && TERMINATORS.includes(paragraph[end]!)) end++;
    while (end < paragraph.length && `»"'’”)]`.includes(paragraph[end]!)) end++;
    // Then the whitespace that separates it from what follows.
    while (end < paragraph.length && /\s/.test(paragraph[end]!)) end++;
    out.push(paragraph.slice(start, end));
    start = end;
    i = end - 1;
  }
  if (start < paragraph.length) out.push(paragraph.slice(start));
  return out.filter((s) => s !== "");
}

/**
 * Titles that end in a full stop without ending a sentence, by the book's
 * language (ADR-0042) — "Mr." in "Mr. Golyadkin". The same lists and rule as
 * the server's alignment (`backend/internal/narration/titles`) and the app's
 * reading line, pinned by `testdata/player/sentences.json`. A language with no
 * list has none, so a Persian book splits exactly as before.
 */
const TITLES: Record<string, ReadonlySet<string>> = {
  en: new Set([
    "Mr",
    "Mrs",
    "Ms",
    "Dr",
    "St",
    "Mme",
    "M",
    "Prof",
    "Capt",
    "Col",
    "Gen",
    "Lt",
    "Sr",
    "Jr",
  ]),
};

const NONE: ReadonlySet<string> = new Set();

function titlesOf(language: string): ReadonlySet<string> {
  return TITLES[language.toLowerCase().split(/[-_]/)[0] ?? ""] ?? NONE;
}

/**
 * Whether the full stop at `dot` ends a whole-word title that a letter
 * follows after white space on the same line: "Mr. and Mrs. Smith" is one
 * sentence; a title before a quote or at the paragraph's end still ends one.
 */
function titleEndsAt(known: ReadonlySet<string>, s: string, dot: number): boolean {
  if (s[dot] !== "." || known.size === 0) return false;
  let from = dot;
  while (from > 0 && LETTER.test(s[from - 1]!)) from--;
  if (!known.has(s.slice(from, dot))) return false;
  let after = dot + 1;
  while (after < s.length && s[after] !== "\n" && /\s/.test(s[after]!)) after++;
  return after > dot + 1 && after < s.length && LETTER.test(s[after]!);
}

const LETTER = /\p{L}/u;
